import { TranscriptionDefaultFormFieldsName } from "@/common/enums/transcriptionDefaultFormFields";
import type { VisitRetentionPeriod } from "@/common/types/settingsOptions";
import type { TranscriptionLanguage } from "@/common/types/transcriptionLanguage";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { TranscriptionSchemaType } from "@/schemas/transcription.schema";
import { Label } from "@radix-ui/react-label";
import { t } from "i18next";
import { useFormContext } from "react-hook-form";

interface Props {
  onChange: (data: Partial<TranscriptionSchemaType>) => void;
  isSubmitting: boolean;
  loading: boolean;
  data: {
    transcriptionLanguages: TranscriptionLanguage[];
    styles: {
      value: string;
      label: string;
    }[];
    formats: {
      value: string;
      label: string;
    }[];
    visitRetentionPeriods: VisitRetentionPeriod[];
  };
}

export const TranscriptionDefaultFormFields = ({
  data: { transcriptionLanguages, styles, visitRetentionPeriods, formats },
  ...props
}: Props): React.ReactNode => {
  const { setValue, getValues } = useFormContext<TranscriptionSchemaType>();

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor={TranscriptionDefaultFormFieldsName.STYLE}>
          {t("settings.transcription.defaultStyle")}
        </Label>
        <Select
          disabled={props.isSubmitting}
          name={TranscriptionDefaultFormFieldsName.STYLE}
          value={getValues(TranscriptionDefaultFormFieldsName.STYLE)}
          onValueChange={(value) => {
            props.onChange({ style: value });
          }}
        >
          <SelectTrigger id={TranscriptionDefaultFormFieldsName.STYLE}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {styles.map((lang) => (
              <SelectItem key={lang.value} value={lang.value}>
                {lang.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor={TranscriptionDefaultFormFieldsName.FORMAT}>
          {t("settings.transcription.defaultFormat")}
        </Label>
        <Select
          disabled={props.isSubmitting}
          name={TranscriptionDefaultFormFieldsName.FORMAT}
          value={getValues(TranscriptionDefaultFormFieldsName.FORMAT)}
          onValueChange={(value) => {
            setValue(TranscriptionDefaultFormFieldsName.FORMAT, value);
            props.onChange({ format: value });
          }}
        >
          <SelectTrigger id={TranscriptionDefaultFormFieldsName.FORMAT}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {formats.map((lang) => (
              <SelectItem key={lang.value} value={lang.value}>
                {lang.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label
          htmlFor={TranscriptionDefaultFormFieldsName.TRANSCRIPTION_LANGUAGE}
        >
          {t("settings.transcription.defaultTranscriptionLanguage")}
        </Label>
        <Select
          disabled={props.isSubmitting || props.loading}
          name={TranscriptionDefaultFormFieldsName.TRANSCRIPTION_LANGUAGE}
          value={getValues(
            TranscriptionDefaultFormFieldsName.TRANSCRIPTION_LANGUAGE
          )}
          onValueChange={(value) => {
            const selected = transcriptionLanguages.find(
              (lang) => lang.name === value
            );
            if (selected) {
              setValue("transcriptionLanguageId", selected.id);
              props.onChange({ transcriptionLanguageId: selected.id });
            }
          }}
        >
          <SelectTrigger
            id={TranscriptionDefaultFormFieldsName.TRANSCRIPTION_LANGUAGE}
          >
            <SelectValue
              placeholder={t(
                "settings.transcription.placeholderTranscriptionLanguage"
              )}
            />
          </SelectTrigger>
          <SelectContent>
            {transcriptionLanguages.map((lang) => (
              <SelectItem key={lang.id} value={lang.name}>
                {lang.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label
          htmlFor={TranscriptionDefaultFormFieldsName.VISIT_RETENTION_PERIOD}
        >
          {t("settings.transcription.defaultRetentionPeriod")}
        </Label>
        <Select
          disabled={props.isSubmitting || props.loading}
          name={TranscriptionDefaultFormFieldsName.VISIT_RETENTION_PERIOD}
          value={getValues(
            TranscriptionDefaultFormFieldsName.VISIT_RETENTION_PERIOD
          )}
          onValueChange={(value) => {
            const selected = visitRetentionPeriods.find(
              (visitRetention) => visitRetention.label === value
            );
            if (selected) {
              setValue(
                TranscriptionDefaultFormFieldsName.VISIT_RETENTION_PERIOD_ID,
                selected.id
              );
              props.onChange({ visitRetentionPeriodId: selected.id });
            }
          }}
        >
          <SelectTrigger
            id={TranscriptionDefaultFormFieldsName.VISIT_RETENTION_PERIOD}
          >
            <SelectValue
              placeholder={t(
                "settings.transcription.placeholderRetentionPeriod"
              )}
            />
          </SelectTrigger>
          <SelectContent>
            {visitRetentionPeriods.map((visitRetention) => (
              <SelectItem key={visitRetention.id} value={visitRetention.label}>
                {visitRetention.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
